import React from 'react';
import {
	AreaTag,
	AssigneeChip,
	DueLabel,
	GateBadge,
	DoneChip,
	SourceIcon,
	MailChip,
	PriorityMark,
	SubtaskProgress,
	TriageBadge,
	StatusChip,
	accentStyle,
} from './TaskBits';
import { toPlainText } from './cleanMarkdown';
import { areaMeta } from './taskModel';
import { isPropertyVisible } from './cardProperties';

// Portal Phase C — kanban card (C2).
//
// 2026-07-26: which properties appear is now the USER'S choice (Anthony's call —
// Notion-style), persisted per user. The card kept its layout and gave up its
// opinion about content: `selected` decides what renders, this file decides
// where it lands. The catalog of properties lives in cardProperties.js.
//
// The one opinion it keeps is SUPPRESSION: a property naming the dimension the
// board is grouped by is hidden even when selected, because the column heading
// already says it — twelve cards in the Website column do not each need to say
// "Website". That rule is data on the catalog entry (`grouped`), not a
// conditional here, so the list view can honour the same rule later without
// re-deriving it. Notion has no equivalent; this is a deliberate divergence.
//
// The old hardcoded behaviour swapped area↔status by grouping. That is now the
// natural consequence of selecting both and letting suppression drop whichever
// one the grouping makes redundant, rather than a special case in the markup.
function TaskCard({
	task,
	groupBy,
	childrenOf,
	now,
	selected,
	projectsById = {},
	onPointerDown,
	dragging,
	forwardRef,
}) {
	const children = childrenOf?.(task.id) || [];
	const parked = !!task.gate;
	const done = task.status === 'completed';
	const show = (key) => isPropertyVisible(key, selected, groupBy);

	// Due / gate / done share one slot: they answer the same question ("when, or
	// why not"), and a card showing all three says nothing. Precedence matches
	// the board's own completed > gate > status ordering.
	const showDue = show('due');
	const timeSlot = done ? (
		<DoneChip closedAt={task.closedAt} />
	) : parked ? (
		<GateBadge gate={task.gate} forceLabel />
	) : showDue ? (
		<DueLabel task={task} now={now} />
	) : null;

	// Plain object keyed by id, NOT a Map — matching TaskRow/ProjectDetail. This
	// was written as `projectsById?.get?.(id)` first, which never threw and never
	// resolved: the optional call silently yielded undefined, so the Project chip
	// simply could not appear no matter what was selected. Caught in the browser,
	// not by a test, because nothing about it fails loudly.
	const project = task.projectId ? projectsById[task.projectId] : null;
	const preview = show('description') ? toPlainText(task.description) : '';

	// Mail gets a real chip in the head (Anthony, 2026-09-05: "separate mail
	// from other things" on the board too); the quiet SourceIcon stays for
	// every other source so the Source property still means what it did.
	const isMail = task.source === 'email';
	const head = [
		show('source') && isMail && <MailChip key="mail" task={task} />,
		show('area') && <AreaTag key="area" area={task.area} />,
		show('status') && <StatusChip key="status" status={task.status} />,
		show('triage') && <TriageBadge key="triage" task={task} />,
	].filter(Boolean);

	const headEnd = [
		show('priority') && <PriorityMark key="priority" priority={task.priority} />,
		show('source') && !isMail && <SourceIcon key="source" source={task.source} />,
	].filter(Boolean);

	const foot = [
		show('assignee') && <AssigneeChip key="assignee" task={task} />,
		// Rendered here rather than pulled from TaskBits: there is no Bit for a
		// project, an effort or a student chip — the detail view builds each
		// inline. Kept local until a second surface needs them, at which point
		// they become Bits properly.
		show('project') && project && (
			<span key="project" className="wt-ref" title={project.title}>
				<i className="fa-sharp-duotone fa-solid fa-folder" />
				{project.title}
			</span>
		),
		show('effort') && task.effort && (
			<span key="effort" className="wt-ref">
				<i className="fa-sharp-duotone fa-solid fa-weight-hanging" />
				{task.effort}
			</span>
		),
		show('student') && task.studentName && (
			<span key="student" className="wt-ref" title={task.studentName}>
				<i className="fa-sharp-duotone fa-solid fa-user-music" />
				{task.studentName}
			</span>
		),
		show('comments') && task.commentCount > 0 && (
			<span key="comments" className="wt-ref">
				<i className="fa-sharp-duotone fa-solid fa-comment" />
				{task.commentCount}
			</span>
		),
		timeSlot && <React.Fragment key="time">{timeSlot}</React.Fragment>,
		show('subtasks') && children.length > 0 && (
			<SubtaskProgress key="subtasks" children={children} />
		),
	].filter(Boolean);

	return (
		<div
			ref={forwardRef}
			className={`wt-card${done ? ' is-done' : ''}${dragging ? ' is-dragging' : ''}`}
			style={accentStyle(task.area)}
			// The area accent stripe is the card's only remaining unconditional
			// nod to area. When area is neither selected nor suppressed the card
			// would otherwise lose every trace of it, and the stripe is the one
			// carrier that costs no vertical space.
			aria-label={task.area ? `${task.title} — ${areaMeta(task.area).label}` : task.title}
			onPointerDown={(e) => onPointerDown?.(e, task)}>
			{(head.length > 0 || headEnd.length > 0) && (
				<div className="wt-card__head">
					{head}
					{headEnd.length > 0 && (
						<span className="wt-card__headend">{headEnd}</span>
					)}
				</div>
			)}

			<div className="wt-card__title">{task.title}</div>

			{preview && <div className="wt-card__preview">{preview}</div>}

			{foot.length > 0 && <div className="wt-card__foot">{foot}</div>}
		</div>
	);
}

export default TaskCard;
